import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Company, CompanyActiveCoach, GateConfigElement, InviteLink } from '../../../lib/interfaces/company';
import { CustomLoginFormConfig } from '../../../lib/interfaces/configdata';
import { checkboxRole, CheckboxRole, InviteVerification } from '../../../lib/interfaces/roles';
import { AppdataService } from '../../../service/appdata.service';
import { CompaniesService } from '../../../service/companies.service';
import { SnackService } from '../../../service/snack.service';
import { EditLoginFormsComponent } from '../edit-login-forms/edit-login-forms.component';
import { EditUserGatesComponent } from '../edit-user-gates/edit-user-gates.component';

@Component({
    selector: 'app-generate-invite-link',
    standalone: false,
    templateUrl: './generate-invite-link.component.html',
    styleUrl: './generate-invite-link.component.scss'
})
export class GenerateInviteLinkComponent implements OnInit {
    // Injectioins
    data = inject<{
        roleList: checkboxRole[];
        link: InviteLink | null,
        companyData: Company,
        licenseId: string
        userId: string,
    }>(MAT_DIALOG_DATA);
    dialogRef = inject(MatDialogRef<GenerateInviteLinkComponent>);
    dialog = inject(MatDialog);
    companyService = inject(CompaniesService);
    private snackService = inject(SnackService);
    private appDataService = inject(AppdataService);
    private destroyRef = inject(DestroyRef);

    // Objects
    public loginFormElements: unknown[] = [];
    public roles: checkboxRole[] = [];

    protected loginFormDefault: CustomLoginFormConfig[] = []
    protected usergateConfig: GateConfigElement[] | null = null;
    protected loginFormConfig: CustomLoginFormConfig[] | null = null;
    protected inviteVerifications: CheckboxRole<{ verification: InviteVerification }>[] = [];
    protected coaches: CompanyActiveCoach[] = [];
    protected selectedCoach: string | null = null;

    // Variable
    expire: Date | undefined = undefined;
    minDate: string = '';

    // Functions
    async ngOnInit() {
        this.roles = [...this.data.roleList];

        // Expire date must not be in the past – min selectable date is today
        const today = new Date();
        this.minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const inviteVerifications = Object.values(InviteVerification);

        for (const inviteVerification of inviteVerifications) {
            this.inviteVerifications.push({
                verification: inviteVerification,
                active: !this.data.link ? false : this.data.link.invitationProcessType === inviteVerification,
            });
        }

        const coaches = await this.companyService.getCoachesByCompanyId(this.data.companyData.companyId);

        if (typeof coaches !== 'boolean') {
            this.coaches = coaches;
        }

        // Get Default Config for LoginForm
        this.appDataService.customLoginForm
            .subscribe((data: CustomLoginFormConfig[]) => {
                if (data) {
                    this.loginFormDefault = data;
                }
            });

        this.loginFormConfig = this.data.link?.customLoginFormData ?? null;
        this.selectedCoach = this.data.link?.companyUserId ?? null;
    }

    close() {
        this.dialogRef.close();
    }

    public openUserGates() {
        const usergateDialogRef = this.dialog.open(EditUserGatesComponent, {
            data: {
                companyId: this.data.companyData.companyId,
                userGateConfig: this.usergateConfig,
            },
            panelClass: 'user-gate-screen',
        })
        usergateDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                console.log('UsergAte', result)
                this.usergateConfig = result;
            }
        });
    }

    protected openLoginForm(): void {
        const loginFormDialogRef = this.dialog.open(
            EditLoginFormsComponent,
            {
                data: {
                    inviteLink: this.data.link,
                    customLoginFormConfigData: this.loginFormConfig ?? null,
                },
                panelClass: 'login-form-dialog',
            },
        );

        loginFormDialogRef.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    if (result) {
                        this.loginFormConfig = result;
                    }
                },
            });
    }

    protected selectInviteVerification(verification: InviteVerification): void {
        const selectedVerificationMethod = this.inviteVerifications.find((iv) => iv.verification === verification);

        if (!selectedVerificationMethod) {
            throw Error(`invalid verification ${verification} for invite verifications!`);
        }

        // not more that one checkbox can be active at a time
        if (selectedVerificationMethod.active) {
            this.inviteVerifications.forEach((iv) => {
                if (iv !== selectedVerificationMethod) {
                    iv.active = false;
                }
            });
        }
    }

    protected isStartupFounderSelected(): boolean {
        const isFounderSelected = this.roles.some((role) => role.active && role.id === 'USER');

        if (!isFounderSelected) {
            this.selectedCoach = null;
        }

        return isFounderSelected;
    }

    async generate() {
        console.log(this.data);

        if (!this.inviteVerifications.some((iv) => iv.active)) {
            this.snackService.error({
                headline: 'Missing user verification',
                text: 'Please select a user verification method',
            });

            return;
        }

        const activeVerificationMethod = this.inviteVerifications.find(
            (iv) => iv.active === true,
        )!.verification;

        const roles = this.data.link ? this.data.link.role : this.roles.filter(r => r.active === true);
        const roleIdList: string[] = this.data.link ? this.data.link.role : roles.map(r => (<checkboxRole>r).id);

        if (
            roleIdList.includes('USER') &&
            this.selectedCoach === null
        ) {
            this.snackService.error({
                headline: 'Missing coach',
                text: 'You must select a coach since this invitation seems to be for future founders',
            });

            return;
        }

        let expireToString: number | undefined = undefined;

        if (this.expire || this.data.link?.expire) {
            expireToString = new Date(this.expire || this.data.link!.expire!).getTime();
        }

        let coach: CompanyActiveCoach | undefined = undefined;

        if (this.selectedCoach) {
            coach = this.coaches.find((coach) => coach.id === this.selectedCoach);
        }

        const linkObject: InviteLink = {
            id: "",
            companyId: this.data.companyData.companyId,
            companyUserId: this.selectedCoach ?? undefined,
            expire: expireToString,
            role: roleIdList,
            licenseId: this.data.licenseId ?? this.data.companyData.license?.licenseId,
            invitationProcessType: activeVerificationMethod,
            usergate: this.usergateConfig ? true : false,
            usergateConfig: this.usergateConfig,
            loginForm: this.loginFormConfig ? true : false,
            customLoginFormData: this.loginFormConfig,
            prename: coach?.prename,
            lastname: coach?.lastname,
            email: coach?.email,
        };

        const re = await this.companyService.generateInviteLink(
            this.data.companyData.companyId,
            this.selectedCoach,
            roleIdList,
            this.data.licenseId ?? this.data.companyData.license?.licenseId,
            expireToString,
            activeVerificationMethod,
            this.usergateConfig,
            this.loginFormConfig,
        )

        if (re === false) {
            return;
        }

        linkObject.id = re;

        this.dialogRef.close(linkObject);
    }
}
